import { getGMTTime } from "@utils/product/productValidators";
import { useEffect } from "react";
import { Platform } from "react-native";

const normalizePluginHtml = (raw: string): string =>
  raw
    .replace(/\\r\\n/g, "\n")
    .replace(/\\n/g, "\n")
    .replace(/\\"/g, '"')
    .replace(/[""]/g, "");

const isValidDomAttributeName = (name: string): boolean =>
  name.length > 0 && !/[<>"'`=\s]/.test(name);

const setScriptAttribute = (
  script: HTMLScriptElement,
  name: string,
  value: string
) => {
  if (!isValidDomAttributeName(name)) return;
  try {
    script.setAttribute(name, value.replace(/[""]/g, "").trim());
  } catch {
    // Mobile Safari rejects invalid qualified names from malformed vendor HTML.
  }
};

export const useInjectScript = (
  scriptString: string,
  isApplive?: boolean,
  env?: any
) => {
  useEffect(() => {
    if (
      Platform.OS === "web" &&
      isApplive &&
      !!scriptString &&
      env?.production?.includes(window.location.hostname)
    ) {
      const parser = new DOMParser();
      const doc = parser.parseFromString(
        normalizePluginHtml(scriptString),
        "text/html"
      );

      const injectedScripts: HTMLScriptElement[] = [];
      const injectedElements: HTMLElement[] = [];

      Array.from(doc.body.childNodes).forEach((node) => {
        if (
          node.nodeType === Node.ELEMENT_NODE &&
          (node as HTMLElement).tagName.toLowerCase() !== "script"
        ) {
          const importedElement = document.importNode(
            node,
            true
          ) as HTMLElement;
          document.body.appendChild(importedElement);
          injectedElements.push(importedElement);
        }
      });

      const scripts = Array.from(doc.querySelectorAll("script"));

      const loadScriptsSequentially = async () => {
        for (const scriptTag of scripts) {
          await new Promise<void>((resolve) => {
            const script = document.createElement("script");

            Array.from(scriptTag.attributes).forEach((attr) => {
              if (attr.name !== "src") {
                setScriptAttribute(script, attr.name, attr.value);
              }
            });

            const rawSrc = scriptTag.getAttribute("src") || "";
            const src = rawSrc.replace(/[“”]/g, "").trim();

            if (src) {
              script.src = src;
              script.async = true;
              script.onload = () => resolve();
              script.onerror = () => resolve();
            } else {
              script.textContent = scriptTag.textContent;
              resolve();
            }

            document.head.appendChild(script);
            injectedScripts.push(script);
          });
        }
      };

      const rafId = requestAnimationFrame(() => {
        loadScriptsSequentially();
      });

      return () => {
        cancelAnimationFrame(rafId);

        injectedScripts.forEach((script) => {
          if (script.parentNode) script.parentNode.removeChild(script);
        });

        injectedElements.forEach((el) => {
          if (el.parentNode) el.parentNode.removeChild(el);
        });
      };
    }
  }, [scriptString, isApplive]);
};

export const useInjectMetaTags = () => {
  useEffect(() => {
    if (Platform.OS !== "web") return;

    const gmtTime = getGMTTime();
    const metaTags = [
      {
        httpEquiv: "Cache-Control",
        content: "max-age=0, no-cache, no-store, must-revalidate",
      },
      { httpEquiv: "Pragma", content: "no-cache" },
      { httpEquiv: "Expires", content: gmtTime },
    ];

    metaTags.forEach(({ httpEquiv, content }) => {
      let meta = document.querySelector(
        `meta[http-equiv="${httpEquiv}"]`
      ) as HTMLMetaElement | null;
      if (!meta) {
        meta = document.createElement("meta") as HTMLMetaElement;
        meta.httpEquiv = httpEquiv;
        meta.content = content;
        document.head.appendChild(meta);
      } else {
        meta.content = content;
      }
    });
  });
};

export const useInjectMetaTagsAndDatadog = () => {
  const isWeb = process.env.EXPO_PUBLIC_PLATFORM === "web";

  useEffect(() => {
    if (
      !isWeb ||
      typeof window === "undefined" ||
      typeof document === "undefined"
    )
      return;

    // Prevent duplicate script injection
    if (document.getElementById("datadog-rum-inline-script")) return;

    const script = document.createElement("script");
    script.id = "datadog-rum-inline-script";
    script.type = "text/javascript";

    const version = `${process.env.EXPO_PUBLIC_DATADOG_APP_VERSION}.${process.env.EXPO_PUBLIC_BUILD_DATE}`;
    const client_token = `${process.env.EXPO_PUBLIC_DATADOG_CLIENT_TOKEN}`;
    const application_id = `${process.env.EXPO_PUBLIC_DATADOG_APPLICATION_ID}`;
    const service_name = `${process.env.EXPO_PUBLIC_DATADOG_SERVICE_NAME}`;
    const site = "datadoghq.com";
    const sessionSampleRate = 20;
    const sessionReplaySampleRate = 5;
    const env = `${process.env.EXPO_PUBLIC_DATADOG_ENVIRONMENT}`;
    const allowedTracingUrls = process.env.EXPO_PUBLIC_DATADOG_TRACING_URLS
      ? process.env.EXPO_PUBLIC_DATADOG_TRACING_URLS.split(",")
          .map((url) => `'${url.trim()}'`)
          .join(",")
      : "";
    const trackResources = true;
    const trackLongTasks = true;
    const trackUserInteractions = true;
    const traceSampleRate = 100;

    script.innerHTML = `
      (function (h, o, u, n, d) {
        h = h[d] = h[d] || { q: [], onReady: function (c) { h.q.push(c) } }
        d = o.createElement(u); d.async = 1; d.src = n
        n = o.getElementsByTagName(u)[0]; n.parentNode.insertBefore(d, n)
      })(window, document, 'script', 'https://www.datadoghq-browser-agent.com/us1/v6/datadog-rum.js', 'DD_RUM');

      window.DD_RUM.onReady(function () {
        window.DD_RUM.init({
          clientToken: '${client_token}',
          applicationId: '${application_id}',
          site: '${site}',
          service: '${service_name}',
          env: '${env}',
          version: '${version}',
          sessionSampleRate: ${sessionSampleRate},
          sessionReplaySampleRate: ${sessionReplaySampleRate},
          allowedTracingUrls: [${allowedTracingUrls}],
          defaultPrivacyLevel: 'mask-user-input',
          trackResources: ${trackResources},
          trackLongTasks: ${trackLongTasks},
          trackUserInteractions: ${trackUserInteractions},
          traceSampleRate: ${traceSampleRate},
        });
      });
    `;

    document.head.appendChild(script);
  }, []);
};
